// src/features/admin/pages/AdminLoginPage.tsx
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { login, meWithRoles, logout, api } from "@/lib/api";
import { COLOR, FW, RADIUS } from "@/ui/tokens";

const withBase = (p: string) => (import.meta.env.BASE_URL || "./") + p.replace(/^\/+/, "");
const LOGO = withBase("icons/ReadersNote_logo.svg");

export default function AdminLoginPage() {
    const [email, setEmail] = useState("");
    const [pw, setPw] = useState("");
    const [showPw, setShowPw] = useState(false);
    const [err, setErr] = useState<string | null>(null);
    const [loading, setLoading] = useState(false);
    const [checking, setChecking] = useState(true);
    const nav = useNavigate();

    useEffect(() => {
        const access = localStorage.getItem("bt.access");
        if (!access) { setChecking(false); return; }
        (async () => {
            try {
                const data = await meWithRoles();
                const roles: string[] = (data?.roles || []).map((r: any) => r.code || r);
                if (roles.some((r) => ["ADMIN", "SUPER_ADMIN", "AUTHOR", "MONITORING"].includes(r))) {
                    nav("/admin", { replace: true });
                    return;
                }
            } catch {}
            logout();
            setChecking(false);
        })();
    }, [nav]);

    const onSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (loading) return;
        setLoading(true); setErr(null);
        try {
            await login(email.trim(), pw);
            const data = await meWithRoles();
            const roles: string[] = (data?.roles || []).map((r: any) => r.code || r);
            if (!roles.some((r) => ["ADMIN", "SUPER_ADMIN", "AUTHOR", "MONITORING"].includes(r))) {
                try {
                    await api("/auth/logout", { method: "POST", json: { refresh_token: localStorage.getItem("bt.refresh") } });
                } catch {}
                logout();
                setErr("어드민 권한이 없습니다.");
                return;
            }
            nav("/admin", { replace: true });
        } catch (e: any) {
            setErr(e?.message || "로그인 실패");
        } finally {
            setLoading(false);
        }
    };

    if (checking) {
        return (
            <div style={styles.wrap}>
                <span style={{ color: COLOR.textMuted, fontSize: 14 }}>확인 중…</span>
            </div>
        );
    }

    return (
        <div style={styles.wrap}>
            <div style={styles.card}>
                {/* Logo + title */}
                <div style={styles.header}>
                    <div style={styles.logoBox}>
                        <img src={LOGO} alt="" style={{ width: 28, height: 28, filter: "invert(1)" }} />
                    </div>
                    <h1 style={styles.title}>Admin Portal</h1>
                    <p style={styles.subtitle}>독서노트 관리자 전용</p>
                </div>

                <form onSubmit={onSubmit} style={styles.form}>
                    <div style={styles.fieldGroup}>
                        <label style={styles.label}>이메일</label>
                        <input
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            placeholder="admin@example.com"
                            inputMode="email"
                            autoComplete="username"
                            style={styles.input}
                            onFocus={e => (e.currentTarget.style.borderColor = "#18181b")}
                            onBlur={e => (e.currentTarget.style.borderColor = "#e4e4e7")}
                        />
                    </div>

                    <div style={styles.fieldGroup}>
                        <label style={styles.label}>비밀번호</label>
                        <div style={{ position: "relative" }}>
                            <input
                                value={pw}
                                onChange={(e) => setPw(e.target.value)}
                                placeholder="••••••••"
                                type={showPw ? "text" : "password"}
                                autoComplete="current-password"
                                style={{ ...styles.input, paddingRight: 44 }}
                                onFocus={e => (e.currentTarget.style.borderColor = "#18181b")}
                                onBlur={e => (e.currentTarget.style.borderColor = "#e4e4e7")}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPw(v => !v)}
                                style={styles.eyeBtn}
                                tabIndex={-1}
                            >
                                {showPw ? "🙈" : "👁️"}
                            </button>
                        </div>
                    </div>

                    {err && (
                        <div style={styles.errorBox}>
                            <span style={{ fontSize: 13 }}>⚠ {err}</span>
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={!email || !pw || loading}
                        style={{
                            ...styles.submitBtn,
                            opacity: (!email || !pw || loading) ? 0.55 : 1,
                        }}
                    >
                        {loading ? "로그인 중…" : "로그인"}
                    </button>
                </form>
            </div>
        </div>
    );
}

const styles: Record<string, React.CSSProperties> = {
    wrap: {
        minHeight: "100vh",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: COLOR.bgLighter,
    },
    card: {
        width: 380,
        background: COLOR.white,
        borderRadius: RADIUS.lg,
        boxShadow: "0 4px 24px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)",
        overflow: "hidden",
    },
    header: {
        background: COLOR.textDark,
        padding: "32px 32px 28px",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 8,
    },
    logoBox: {
        width: 48,
        height: 48,
        background: "#27272a",
        borderRadius: 12,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        marginBottom: 4,
    },
    title: {
        margin: 0,
        fontSize: 22,
        fontWeight: FW.extraBold,
        color: COLOR.white,
        letterSpacing: "-0.4px",
    },
    subtitle: {
        margin: 0,
        fontSize: 13,
        color: COLOR.textMuted,
        fontWeight: 500,
    },
    form: {
        padding: "28px 32px 32px",
        display: "flex",
        flexDirection: "column",
        gap: 16,
    },
    fieldGroup: {
        display: "flex",
        flexDirection: "column",
        gap: 6,
    },
    label: {
        fontSize: 12,
        fontWeight: FW.bold,
        color: "#3f3f46",
        letterSpacing: "0.3px",
        textTransform: "uppercase" as const,
    },
    input: {
        width: "100%",
        height: 42,
        padding: "0 12px",
        border: "1.5px solid #e4e4e7",
        borderRadius: RADIUS.sm,
        fontSize: 14,
        fontWeight: 500,
        color: COLOR.textDark,
        background: "#fafafa",
        outline: "none",
        transition: "border-color 0.15s",
        boxSizing: "border-box",
    },
    eyeBtn: {
        position: "absolute",
        right: 10,
        top: "50%",
        transform: "translateY(-50%)",
        background: "none",
        border: "none",
        cursor: "pointer",
        fontSize: 15,
        lineHeight: 1,
        padding: 2,
    },
    errorBox: {
        background: "#fef2f2",
        border: "1px solid #fecaca",
        borderRadius: 8,
        padding: "10px 12px",
        color: "#dc2626",
        fontWeight: 600,
    },
    submitBtn: {
        height: 44,
        background: COLOR.textDark,
        color: COLOR.white,
        border: "none",
        borderRadius: RADIUS.sm,
        fontSize: 14,
        fontWeight: FW.bold,
        cursor: "pointer",
        transition: "opacity 0.15s",
        marginTop: 4,
    },
};